import { Component, EventEmitter, Output, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SocialService } from '../../../../../../../../services/social.service';
import { Subject, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import { ToastService } from '../../../../../../../../services/toast.service';
import { LoaderComponent } from '../../../../../../reusable/loader/loader.component'
import { BackButtonComponent } from '../../../../../../../../shared/components/buttons/back-button/back-button.component';
import { ConfirmButtonComponent } from '../../../../../../../../shared/components/buttons/confirm-button/confirm-button.component';
import { ExitButtonComponent } from '../../../../../../../../shared/components/buttons/exit-button/exit-button.component';


@Component({
  selector: 'app-add-members',
  standalone: true,
  imports: [CommonModule, FormsModule, LoaderComponent, BackButtonComponent, ConfirmButtonComponent, ExitButtonComponent],
  templateUrl: './add-members.component.html',
  styleUrl: './add-members.component.scss',
})
export class AddMembersComponent implements OnInit {
  @Input() fleet: any;
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<any>();

  activeTab: 'send' | 'pending' | 'requests' = 'send';
  searchQuery = '';
  invitationMessage = '';
  isLoading = false;

  selectedMembers: any[] = [];
  suggestedMembers: any[] = [];

  pendingInvites: any[] = [];
  joinRequests: any[] = [];

  // Loading flag for the invite Save confirm button (separate from search `isLoading`).
  isSaving = false;

  private searchSubject = new Subject<string>();

  constructor(
    private socialService: SocialService,
    public toast: ToastService,
  ) { }

  getInitials(name: string): string {
    if (!name) return '??';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0][0].toUpperCase();
  }

  formatTime(dateStr: string): string {
    if (!dateStr) return '';
    // Ensure the date string is treated as UTC if it doesn't have a timezone indicator
    let normalizedDateStr = dateStr;
    if (!normalizedDateStr.endsWith('Z') && !normalizedDateStr.includes('+')) {
      normalizedDateStr = normalizedDateStr + 'Z';
    }
    const date = new Date(normalizedDateStr);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    const days = Math.floor(diffInSeconds / 86400);
    if (days < 7) return `${days}d ago`;
    return `${Math.floor(days / 7)}w ago`;
  }

  fetchPendingInvites() {
    this.isLoading = true;
    this.socialService.getOutgoingInvites().subscribe({
      next: (response: any) => {
        this.pendingInvites = (response.results || []).map((req: any) => ({
          id: req._id,
          name: req.target?.name || 'Unknown',
          image: req.target?.image_url,
          group: req.target?.vessel_name || 'No vessel', // Default if missing
          time: this.formatTime(req.requested_at),
          initials: this.getInitials(req.target?.name),
        }));
        this.isLoading = false;
      },
      error: (err: any) => {
        console.error('Error fetching pending invites:', err);
        this.isLoading = false;
      },
    });
  }

  fetchJoinRequests() {
    this.isLoading = true;
    this.socialService.getJoinRequests().subscribe({
      next: (response: any) => {
        this.joinRequests = (response.results || []).map((req: any) => ({
          id: req._id,
          name: req.requester?.name || 'Unknown',
          image: req.requester?.image_url,
          group: req.requester?.vessel_name || 'No vessel', // Corrected here too
          time: this.formatTime(req.requested_at),
          initials: this.getInitials(req.requester?.name),
        }));
        this.isLoading = false;
      },
      error: (err: any) => {
        console.error('Error fetching join requests:', err);
        this.isLoading = false;
      },
    });
  }

  ngOnInit() {
    this.fetchPendingInvites();
    this.fetchJoinRequests();
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((query) => {
          if (!query.trim() || query.trim().length < 2) {
            this.suggestedMembers = [];
            this.isLoading = false;
            return [];
          }
          if (!this.fleet?._id) {
            console.warn('Fleet ID missing for invite search');
            return [];
          }
          this.isLoading = true;
          return this.socialService.inviteSearch(this.fleet._id, query);
        }),
      )
      .subscribe({
        next: (response: any) => {
          this.suggestedMembers = (response.results || []).map((user: any) => ({
            id: user.user_id, // Use user_id as requested (the UUID)
            dbId: user._id, // Keep _id if needed
            name: user.name,
            initials: this.getInitials(user.name),
            image: user.image_url,
            selected: this.selectedMembers.some((sm) => sm.id === user.user_id),
          }));
          this.isLoading = false;
        },
        error: (err: any) => {
          console.error('Search error:', err);
          this.isLoading = false;
        },
      });
  }

  onSearchChange(query: string) {
    this.searchSubject.next(query);
  }

  setTab(tab: 'send' | 'pending' | 'requests') {
    this.activeTab = tab;
    if (tab === 'pending') {
      this.fetchPendingInvites();
    } else if (tab === 'requests') {
      this.fetchJoinRequests();
    }
  }

  toggleMemberSelection(member: any) {
    member.selected = !member.selected;
    if (member.selected) {
      if (!this.selectedMembers.find((m) => m.id === member.id)) {
        this.selectedMembers.push(member);
      }
    } else {
      this.removeMember(member);
    }
  }

  removeMember(member: any) {
    this.selectedMembers = this.selectedMembers.filter(
      (m) => m.id !== member.id,
    );
    const suggested = this.suggestedMembers.find((m) => m.id === member.id);
    if (suggested) suggested.selected = false;
  }

  cancelInvite(inviteId: string) {
    const originalInvites = [...this.pendingInvites];
    // Optimistic update
    this.pendingInvites = this.pendingInvites.filter((i) => i.id !== inviteId);

    this.socialService.cancelRequest(inviteId).subscribe({
      next: () => {
        this.showSuccess('Invitation canceled successfully');
      },
      error: (err: any) => {
        console.error('Error cancelling invite:', err);
        this.showError('Error cancelling invite');
        this.pendingInvites = originalInvites; // Rollback
      },
    });
  }

  respondToJoinRequest(requestId: string, status: 'accepted' | 'ignored') {
    const originalRequests = [...this.joinRequests];
    // Optimistic update
    this.joinRequests = this.joinRequests.filter((r) => r.id !== requestId);

    this.socialService.respondToRequest(requestId, status).subscribe({
      next: () => {
        const msg =
          status === 'accepted' ? 'Request accepted' : 'Request declined';
        this.showSuccess(msg);
      },
      error: (err: any) => {
        console.error('Error responding to request:', err);
        this.showError('Error processing request');
        this.joinRequests = originalRequests; // Rollback
      },
    });
  }

  closePopup() {
    this.close.emit();
  }
  // ---------- SNACKBAR ----------
  private showError(message: string) {
    this.toast.error(message);
  }

  private showSuccess(message: string) {
    this.toast.success(message);
  }

  save() {
    if (this.isSaving) return;
    if (this.selectedMembers.length === 0 || !this.fleet?._id) return;

    this.isSaving = true;
    const payload = {
      user_ids: this.selectedMembers.map((m) => m.id),
      message: this.invitationMessage,
    };

    this.socialService.inviteToCommunity(this.fleet._id, payload).subscribe({
      next: (res) => {
        this.isSaving = false;
        this.selectedMembers = [];
        this.invitationMessage = '';
        this.searchQuery = '';
        this.suggestedMembers = [];
        // Clear selected state in suggested list
        this.suggestedMembers.forEach((m) => (m.selected = false));
        this.saved.emit(res);
      },
      error: (err: any) => {
        console.error('Error inviting members:', err);
        this.showError('Error inviting members');
        this.isSaving = false;
      },
    });
  }
}
